import React, { useEffect, useRef, useState } from 'react';

/**
 * Animated counter that runs when the element scrolls into view.
 * Accepts values like "20+", "20,000+", "70+", "2003" etc.
 */
const CountUp = ({ value, duration = 1600 }) => {
  const [display, setDisplay] = useState('0');
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    // Parse number + suffix  (e.g. "20,000+" → num=20000, suffix="+")
    const match = String(value).match(/^([0-9,]+)(.*)$/);
    if (!match) {
      setDisplay(value);
      return;
    }
    const num = parseInt(match[1].replace(/,/g, ''), 10);
    const suffix = match[2] || '';

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started.current) {
          started.current = true;
          const startTime = performance.now();

          const tick = (now) => {
            const elapsed = now - startTime;
            const progress = Math.min(elapsed / duration, 1);
            // easeOutCubic
            const ease = 1 - Math.pow(1 - progress, 3);
            const current = Math.round(ease * num);
            setDisplay(
              num >= 1000
                ? current.toLocaleString() + suffix
                : current + suffix
            );
            if (progress < 1) requestAnimationFrame(tick);
          };

          requestAnimationFrame(tick);
        }
      },
      { threshold: 0.5 }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [value, duration]);

  return <span ref={ref}>{display}</span>;
};

export default CountUp;
